import { useEffect, useState, useMemo } from 'react'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { useToast } from '@wartek-id/toast'

import { PelaksanaanStepResetCard } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanStepResetCard'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import { assetUrl } from 'utils/url'
import { useAPIPostPelaksanaanAppealApproval } from 'api/kinerja/useAPIKinerja'
import { API_PATH_NOTIFICATION_LIST } from 'api/kinerja/useAPIKinerjaNotification'

import type { TStatePelaksanaan } from 'types/kinerja-pelaksanaan'

type PelaksanaanAppealApprovalDialogProps = Omit<DialogProps, 'children'> & {
  currentStep: TStatePelaksanaan
  requestId?: string
}

export function PelaksanaanAppealApprovalDialog({
  isOpen,
  currentStep,
  onClose,
  requestId,
  ...dialogProps
}: PelaksanaanAppealApprovalDialogProps) {
  const [activeContent, setActiveContent] = useState<
    'CONFIRMATION' | 'SUCCESS'
  >('CONFIRMATION')
  const toast = useToast()
  const queryClient = useQueryClient()

  // ----------------------------------------------------
  // Handle confirmation checkbox
  // ----------------------------------------------------
  const [isConfirmed, setIsConfirmed] = useState(false)
  const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConfirmed(e.target.checked)
  }

  // ----------------------------------------------------
  // Reset dialog upon dismissal
  // ----------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      setActiveContent('CONFIRMATION')
      setIsConfirmed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ----------------------------------------------------
  // Resolve steps to reset
  // ----------------------------------------------------
  const stepsToReset: Array<TStatePelaksanaan> = useMemo(() => {
    let stepsToReset: Array<TStatePelaksanaan> = []

    switch (currentStep) {
      case 'DOCUMENT_PREPARATION':
        stepsToReset = [
          'DOCUMENT_PREPARATION',
          'OBSERVATION_IMPLEMENTATION',
          'DOCUMENT_FOLLOW_UP',
          'DOCUMENT_REFLECTION_FOLLOW_UP',
        ]
        break
      case 'DOCUMENT_FOLLOW_UP':
        stepsToReset = ['DOCUMENT_FOLLOW_UP', 'DOCUMENT_REFLECTION_FOLLOW_UP']
        break
      case 'DOCUMENT_REFLECTION_FOLLOW_UP':
        stepsToReset = ['DOCUMENT_REFLECTION_FOLLOW_UP']
        break
    }

    return stepsToReset
  }, [currentStep])

  // ----------------------------------------------------
  // Resolve dialog size
  // ----------------------------------------------------
  const resolvedDialogSize = activeContent === 'SUCCESS' ? 596 : 800

  // ----------------------------------------------------
  // Handle appeal mutation
  // ----------------------------------------------------
  const appealApprovalMutation = useAPIPostPelaksanaanAppealApproval(requestId)

  const handleConfirm = () => {
    appealApprovalMutation.mutate(
      { confirmationState: 'APPROVED', tipe: 'GPK' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
          queryClient.invalidateQueries(API_PATH_NOTIFICATION_LIST)
        },
        onError: () => {
          onClose()
          toast({ message: 'Gagal lanjut. Coba lagi.' })
        },
      }
    )
  }

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      autoFocus={false}
      customSize={resolvedDialogSize}
      onClose={onClose}
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent
        className="p-6"
        aria-labelledby="appeal-approve-dialog-title"
      >
        {activeContent === 'CONFIRMATION' && (
          <div className="space-y-4">
            <Text variant="display-sm" id="appeal-approve-dialog-title" as="h3">
              Anda akan menerima pengajuan. Berikut hal yang terdampak:
            </Text>
            <div
              className={clsx(
                'grid gap-4 mb-6',
                stepsToReset.length < 2 ? 'grid-cols-1' : 'grid-cols-2'
              )}
            >
              {stepsToReset.map((step, i) => {
                const stepData = PRAKTIK_KINERJA_STEPS[step]

                return (
                  <PelaksanaanStepResetCard
                    title={stepData.title}
                    description={stepData.resetMessage.tk}
                    imagePath={stepData.imagePath}
                    className="max-w-md"
                    key={i}
                  />
                )
              })}
            </div>

            <Checkbox
              value="accept"
              labelPosition="center"
              onChange={handleConfirmationChange}
              checked={isConfirmed}
            >
              <Text as="span" variant="body-bold">
                Saya setuju untuk mengisi ulang hasil penilaian
              </Text>
            </Checkbox>

            <div className="flex space-x-3 border-t pt-4 justify-end">
              <Button
                disabled={appealApprovalMutation.isLoading}
                color="white"
                size="lg"
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                disabled={appealApprovalMutation.isLoading || !isConfirmed}
                isLoading={appealApprovalMutation.isLoading}
                size="lg"
                onClick={handleConfirm}
              >
                Terima dan ulangi hasil penilaian
              </Button>
            </div>
          </div>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl(
                '/illustrations/img-reflection-documents-magnifying-glass.png'
              )}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text
              variant="display-sm"
              className="mb-4"
              id="appeal-approve-dialog-title"
              as="h3"
            >
              Pengajuan perubahan diterima
            </Text>
            <Text
              variant="body-lg"
              color="subdued"
              className="mb-6"
              id="appeal-approve-dialog-description"
            >
              Setelah Pegawai selesai mengubah isi pengisian, Anda dapat
              mengulang hasil penilaian Anda.
            </Text>
            <Button size="lg" onClick={() => onClose?.()}>
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
